import { Banknote, ExternalLink } from 'lucide-react';
import { usePendingPayouts } from '@/hooks/usePendingPayouts';
import { formatCurrency, formatPayoutDate } from './_lib';
import styles from '../DashboardPage.module.css';

/**
 * Pending Stripe Payouts widget. KPI-style summary of the
 * tenant's next bank payout from Stripe.
 *
 * Operator-tier — for third_party_admin the response carries
 * `widget_applies: false` and the page-level grid map skips
 * rendering the slot entirely (in view mode). This component
 * still defends against the same shape in case it's ever
 * mounted directly.
 *
 * Empty-state hierarchy:
 *   1. !widget_applies          → render null (page grid handles this)
 *   2. !has_connect_account     → "Stripe Connect not configured"
 *   3. count === 0              → "No payouts pending"
 *   4. count > 0                → amount + arrival subtitle
 */
export default function PendingPayoutsWidget({ onNavigate }: { onNavigate?: (to: string) => void }) {
  const q = usePendingPayouts();
  const data = q.data;

  if (data && !data.widget_applies) return null;

  return (
    <div className={styles.chartBody}>
      <div className={styles.payoutHeader}>
        <div className={styles.chartIcon}><Banknote size={14} /></div>
        <p className={styles.chartTitle}>Pending payouts</p>
      </div>

      {q.isLoading && !data && (
        <div className={styles.chartPlaceholder}>
          <p className={styles.chartDesc}>Loading…</p>
        </div>
      )}

      {data && !data.has_connect_account && (
        <div>
          <p className={styles.payoutEmptyTitle}>Stripe Connect not configured</p>
          <p className={styles.payoutEmptyDesc}>
            Set up payouts in{' '}
            {/* Real link now that Settings hosts the Payouts panel.
                Pre-this-commit the destination didn't exist — the
                copy pointed operators at a surface that wasn't
                wired up. */}
            <button
              type="button"
              className={styles.payoutEmptyLink}
              onClick={() => onNavigate?.('/settings')}
            >
              Settings
            </button>
            {' '}to start accepting payouts.
          </p>
        </div>
      )}

      {data && data.has_connect_account && data.count === 0 && (
        <div>
          <p className={styles.payoutEmptyTitle}>No payouts pending</p>
          <p className={styles.payoutEmptyDesc}>
            Your next payout will appear here when Stripe schedules it.
          </p>
        </div>
      )}

      {data && data.has_connect_account && data.count > 0 && (
        <div>
          <p className={styles.payoutAmount}>
            {formatCurrency(data.total_amount_cents / 100, data.currency)}
          </p>
          <p className={styles.payoutSubtitle}>
            {data.earliest_arrival_date
              ? `Arriving ${formatPayoutDate(data.earliest_arrival_date)}`
              : 'Arrival date pending'}
          </p>
          {data.count > 1 && (
            <p className={styles.payoutCount}>
              {data.count} payouts
            </p>
          )}
        </div>
      )}

      {data?.stripe_dashboard_payouts_url && (
        <a
          href={data.stripe_dashboard_payouts_url}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.payoutLink}
        >
          View on Stripe <ExternalLink size={11} />
        </a>
      )}
    </div>
  );
}
